/**
 * PricingTab Component
 * Configure pricing for containers and fees
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PricingSchema, type PricingFormData } from '../../schemas/widgetConfigSchema';
import { useUpdatePricing } from '../../hooks/useWidgetConfig';
import { FormSection, FormField } from './shared/FormSection';
import { Button } from '../ui/button';
import { MATERIAL_TYPES, MATERIAL_TYPE_LABELS } from '../../types/widget-config';
import type { WidgetConfiguration } from '../../types/widget-config';

export interface PricingTabProps {
  config: WidgetConfiguration;
}

/**
 * PricingTab allows providers to configure all pricing settings
 */
export function PricingTab({ config }: PricingTabProps) {
  const updatePricing = useUpdatePricing();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = useForm<PricingFormData>({
    resolver: zodResolver(PricingSchema),
    defaultValues: config.pricing,
  });

  const onSubmit = (data: PricingFormData) => {
    updatePricing.mutate(data);
  };

  // Get available container sizes from services
  const containerSizes = config.services.map(s => s.containerSize);

  const formatPercent = (value: number | undefined): string => {
    if (value === undefined) return '';
    return `${(value * 100).toFixed(2)}%`;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      {/* Base Pricing */}
      <FormSection
        title="Container Base Pricing"
        description="Set rental prices for each container size"
      >
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Container Size
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Base Price
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {containerSizes.map((size) => (
                <tr key={size}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {size} Dumpster
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">$</span>
                      <input
                        {...register(`basePricing.${size}`, {
                          valueAsNumber: true,
                        })}
                        type="number"
                        min="0"
                        step="0.01"
                        className="block w-32 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="0.00"
                      />
                    </div>
                    {errors.basePricing?.[size] && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.basePricing[size]?.message}
                      </p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </FormSection>

      {/* Delivery & Rental */}
      <FormSection
        title="Delivery & Rental Period"
        description="Configure delivery fees and rental duration"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Delivery Fee"
            description="Flat delivery charge"
            error={errors.deliveryFee?.message}
            htmlFor="deliveryFee"
          >
            <div className="flex items-center gap-2">
              <span className="text-gray-500">$</span>
              <input
                {...register('deliveryFee', { valueAsNumber: true })}
                id="deliveryFee"
                type="number"
                min="0"
                step="0.01"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="0.00"
              />
            </div>
          </FormField>

          <FormField
            label="Rental Period"
            description="Days included in base price"
            required
            error={errors.rentalPeriodDays?.message}
            htmlFor="rentalPeriodDays"
          >
            <div className="flex items-center gap-2">
              <input
                {...register('rentalPeriodDays', { valueAsNumber: true })}
                id="rentalPeriodDays"
                type="number"
                min="1"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="7"
              />
              <span className="text-gray-500 whitespace-nowrap">days</span>
            </div>
          </FormField>

          <FormField
            label="Overage Fee (per day)"
            description="Daily charge after rental period"
            error={errors.overageFeePerDay?.message}
            htmlFor="overageFeePerDay"
          >
            <div className="flex items-center gap-2">
              <span className="text-gray-500">$</span>
              <input
                {...register('overageFeePerDay', { valueAsNumber: true })}
                id="overageFeePerDay"
                type="number"
                min="0"
                step="0.01"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="0.00"
              />
              <span className="text-gray-500 whitespace-nowrap">/ day</span>
            </div>
          </FormField>

          <FormField
            label="Environmental Fee"
            description="Optional environmental surcharge"
            error={errors.environmentalFee?.message}
            htmlFor="environmentalFee"
          >
            <div className="flex items-center gap-2">
              <span className="text-gray-500">$</span>
              <input
                {...register('environmentalFee', { valueAsNumber: true })}
                id="environmentalFee"
                type="number"
                min="0"
                step="0.01"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="0.00"
              />
            </div>
          </FormField>
        </div>
      </FormSection>

      {/* Disposal Fees */}
      <FormSection
        title="Disposal Fees (Optional)"
        description="Price per ton for different material types"
      >
        <div className="space-y-3">
          {MATERIAL_TYPES.map((material) => (
            <div key={material} className="flex items-center gap-4">
              <label className="flex-1 text-sm font-medium text-gray-900">
                {MATERIAL_TYPE_LABELS[material]}
              </label>
              <div className="flex items-center gap-2 w-48">
                <span className="text-gray-500">$</span>
                <input
                  {...register(`disposalFees.${material}`, {
                    valueAsNumber: true,
                  })}
                  type="number"
                  min="0"
                  step="0.01"
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="0.00"
                />
                <span className="text-gray-500 whitespace-nowrap">/ ton</span>
              </div>
            </div>
          ))}
        </div>
      </FormSection>

      {/* Tax Rate */}
      <FormSection
        title="Tax Rate"
        description="Sales tax applied to orders (as decimal, e.g., 0.0825 for 8.25%)"
      >
        <FormField
          label="Tax Rate"
          error={errors.taxRate?.message}
          htmlFor="taxRate"
        >
          <div className="flex items-center gap-2">
            <input
              {...register('taxRate', { valueAsNumber: true })}
              id="taxRate"
              type="number"
              min="0"
              max="1"
              step="0.0001"
              className="block w-full md:w-48 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="0.0825"
            />
            <span className="text-sm text-gray-600">
              ({formatPercent(watch('taxRate'))})
            </span>
          </div>
        </FormField>
      </FormSection>

      {/* Save Button */}
      <div className="flex justify-end gap-3 pt-6 border-t">
        <Button
          type="submit"
          disabled={!isDirty || updatePricing.isPending}
          className="min-w-[120px]"
        >
          {updatePricing.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Unsaved Changes Warning */}
      {isDirty && (
        <p className="text-sm text-amber-600" role="status">
          You have unsaved changes
        </p>
      )}
    </form>
  );
}
